import React, { useEffect, useState } from 'react'
import './Users.css'
import { useThemeContext } from '../../../Context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { DARKCOLORS, LIGHTCOLORS } from '../../../Utils/constants';
import { Box, Button, InputAdornment, TablePagination, TextField } from '@mui/material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import SearchIcon from "@mui/icons-material/Search";
import DeleteConfirmationPopup from '../../../Components/Common/DeleteConfirmationPopup';
import SuccessNotification from '../../../Components/Common/SuccessNotification';
import { deleteUser, getUsers } from '../../../Services/authService.js';
import TableComponent from '../MembershipCards/TableComponent.js';
import UsersModal from './UsersModal.js';
import LoadingAnimation from '../../../Components/Common/LoadingAnimation.js';

function Users() {
  const { mode } = useThemeContext();
  const { t } = useTranslation();

  const containerBackground = mode === 'dark' ? '#2D2D2D' : '#ffffff';
  const searchBackground = mode === 'dark' ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
  const scroll1 = mode === 'light' ? LIGHTCOLORS.BodyDarkBackground : DARKCOLORS.BodyLightBackground;
  const scroll = mode === 'light' ? LIGHTCOLORS.GreyScale40 : DARKCOLORS.GreyScale40;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

 const columns = [
  { field: "firstName", label: t("loginPage.firstname") },
  { field: "lastName", label: t("loginPage.lastname") },
  { field: "email", label: t("membershipCards.email") },
  { field: "mobile", label: t("membershipCards.mobileNumber") },
  { field: "address", label: t("users.address") },
  { field: "authorities", label: t("users.role") },
];

  const handleOpenModal = () => {
    setSelectedUser(null)
    setIsModalOpen(true)
  };
  const handleCloseModal = () => setIsModalOpen(false);

  const fetchUsers = async () => {
    try {
      const data = await getUsers(page, rowsPerPage, searchTerm);
      setRows(data.content);
      setTotalRows(data.totalElements);
    } catch (error) {
      console.error(t("users.errorFetchingUsers"), error);
    }
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    fetchUsers();
  }, [page, rowsPerPage, searchTerm]);

  const handleEditClick = (row) => {
    setSelectedUser(row);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (row) => {
    setSelectedRow(row);
    setIsDeletePopupOpen(true);
  };

  const handleCloseDeletePopup = () => {
    setIsDeletePopupOpen(false);
    setSelectedRow(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedRow) return;

    try {
      setLoading(true)
      await deleteUser(selectedRow.login); // Call the delete API
      setSuccessMessage(` ${t('users.user')} ${t('serviceRequest.successMessage2')}`);
      setNotificationOpen(true); // Show success notification
      await fetchUsers(); // Refresh the table data
      handleCloseDeletePopup(); // Close the delete confirmation popup
    } catch (error) {
      setErrorMessage(t('users.Faileddeleteuser'));
    } finally {
      setLoading(false)
    }
  };

  const handleCloseNotification = () => {
    setNotificationOpen(false);
    setSuccessMessage('');
  };

  // Handle individual row selection
  const handleRowSelect = (row) => {
    if (selectedRows.some((selectedRow) => selectedRow.id === row.id)) {
      setSelectedRows(selectedRows.filter((selectedRow) => selectedRow.id !== row.id));
    } else {
      setSelectedRows([...selectedRows, row]);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(0); // searching always shows matches from the first page
  };

  // Handle "Select All"
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows([]); // Deselect all
    } else {
      setSelectedRows(rows); // Select all
    }
    setSelectAll(!selectAll);
  };

  return (
    <div className='dashboard-container-overview'>
      {loading && <LoadingAnimation />}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <h2 className='membership-text-heading'>{t('users.users')}</h2>
        <Button className='export-sales-btn' onClick={handleOpenModal} endIcon={<AddCircleOutlineOutlinedIcon style={{ marginTop: "1px" }} />}>
          {t('users.newUser')}
        </Button>
      </Box>

      <Box style={{ background: containerBackground, borderRadius: "12px", marginTop: "1.5rem" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2 }}>
          <TextField
            className='membership-search-bar'
            placeholder={t("users.searchByFields")}
            value={searchTerm}
            onChange={handleSearchChange}
            style={{ borderRadius: "24px", background: searchBackground, width: "calc(12rem + 12vw)" }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: "20px" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none", // Removes the border from the outline
                },
              },
            }}
          />

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>

          </Box>
        </Box>
        {loading ? (
          <p>{t("loading")}</p>
        ) : (
          <>
           <TableComponent
              rows={rows || []}
              columns={columns || []}
              onEdit={handleEditClick}
              onDelete={handleDeleteClick}
              onRowSelect={handleRowSelect}
              onSelectAll={handleSelectAll}
              selectedRows={selectedRows || []}
              selectAll={selectAll}
              showDelete
            />
            <TablePagination
              component="div"
              count={totalRows}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              labelRowsPerPage={t('membershipCards.RowsPerPage', 'Rows Per Page')}
              rowsPerPageOptions={[10, 25, 50, 100]}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                display: 'flex',
                justifyContent: 'center', // Center the pagination controls
                alignItems: 'center',
                overflowX: 'auto',
                // Same custom scrollbar as the table container
                '&::-webkit-scrollbar': {
                  width: '10px !important',
                  height: '10px !important',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: scroll1,
                  borderRadius: '10px',
                  border: `1px solid ${scroll}`,
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: scroll,
                  borderRadius: '10px',
                  border: '3px solid transparent',
                  backgroundClip: 'padding-box',
                  '&:hover': {
                    backgroundColor: scroll,
                  },
  //                   const scroll1 = mode === 'light' ? LIGHTCOLORS.BodyDarkBackground : DARKCOLORS.BodyLightBackground;
  // const scroll = mode === 'light' ? LIGHTCOLORS.GreyScale40 : DARKCOLORS.GreyScale40;

                },
              }}
            />
          </>
        )}
      </Box>

      {/* Modal Popup */}
      <UsersModal open={isModalOpen} onClose={handleCloseModal} selectedUser={selectedUser} fetchUsers={fetchUsers} />

      {/* Delete Confirmation Popup */}
      <DeleteConfirmationPopup
        open={isDeletePopupOpen}
        onClose={handleCloseDeletePopup}
        heading={t('users.deleteConfirmHeading')}
        message={t('users.deleteConfirmMessage')}
        onConfirm={handleConfirmDelete}
        itemName={`(${selectedRow?.id})`}
        errorMessage={errorMessage}
      />

      {/* Success Notification */}
      <SuccessNotification
        open={notificationOpen}
        message={successMessage}
        onClose={handleCloseNotification}
      />
    </div>
  )
}

export default Users